import { useState, useEffect, useMemo } from "react";
import { alpha, useTheme } from "@mui/material/styles";
import {
    Snackbar, Select, InputLabel, FormControl, Chip, OutlinedInput,
    MenuItem, ListItemText, Checkbox, TextField, Button, Container,
    Typography, Box, Paper, Grid, Tabs, Tab, Alert, Dialog, DialogTitle, DialogContent, DialogActions,
    Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel,
    Toolbar, IconButton, Tooltip, CircularProgress, InputAdornment
} from '@mui/material';
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import { visuallyHidden } from "@mui/utils";
import api from '../../api';
import { useDebounce } from "../../hooks/useDebounce";

// ... Comparator Functions ...
function descendingComparator(a, b, orderBy) {
    let valA = a[orderBy] || "";
    let valB = b[orderBy] || "";

    if (orderBy === 'roles') {
        valA = a.roles ? a.roles.map(r => r.name || r).join(", ") : "";
        valB = b.roles ? b.roles.map(r => r.name || r).join(", ") : "";
    }

    if (orderBy === 'name') {
        valA = `${a.firstName || ""} ${a.lastName || ""}`.trim();
        valB = `${b.firstName || ""} ${b.lastName || ""}`.trim();
    }

    if (typeof valA === 'string') valA = valA.toLowerCase();
    if (typeof valB === 'string') valB = valB.toLowerCase();

    if (valB < valA) return -1;
    if (valB > valA) return 1;
    return 0;
}

function getComparator(order, orderBy) {
    return order === "desc"
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

const headCells = [
    { id: "username", numeric: false, disablePadding: true, label: "Username" },
    { id: "email", numeric: false, disablePadding: false, label: "Email" },
    { id: "name", numeric: false, disablePadding: false, label: "Name" },
    { id: "dealership", numeric: false, disablePadding: false, label: "Dealership" },
    { id: "roles", numeric: false, disablePadding: false, label: "Roles" },
    { id: "actions", numeric: false, disablePadding: false, label: "Actions" },
];

function EnhancedTableHead(props) {
    const { order, orderBy, onRequestSort } = props;
    const createSortHandler = (property) => (event) => onRequestSort(event, property);

    return (
        <TableHead>
            <TableRow>
                {headCells.map((headCell) => (
                    <TableCell
                        key={headCell.id}
                        align={headCell.numeric ? "right" : "left"}
                        padding={headCell.disablePadding ? "none" : "normal"}
                        sortDirection={orderBy === headCell.id ? order : false}
                        sx={headCell.id === 'username' ? { pl: 3 } : {}}
                    >
                        {headCell.id === "actions" ? (
                            headCell.label
                        ) : (
                            <TableSortLabel
                                active={orderBy === headCell.id}
                                direction={orderBy === headCell.id ? order : "asc"}
                                onClick={createSortHandler(headCell.id)}
                            >
                                {headCell.label}
                                {orderBy === headCell.id ? (
                                    <Box component="span" sx={visuallyHidden}>
                                        {order === "desc" ? "sorted descending" : "sorted ascending"}
                                    </Box>
                                ) : null}
                            </TableSortLabel>
                        )}
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

function EnhancedTableToolbar(props) {
    const {
        searchQuery,
        setSearchQuery,
        handleAddUserClick,
        handleAddDealerClick
    } = props;

    return (
        <Toolbar sx={{ pl: { sm: 2 }, pr: { xs: 1, sm: 1 }, pt: 2, pb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                <Typography sx={{ flexShrink: 0 }} variant="h6" id="tableTitle" component="div">FLIGHTSCAN USERS</Typography>

                <TextField
                    variant="outlined"
                    size="small"
                    placeholder="Search Users..."
                    value={searchQuery || ""}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{ ml: 'auto', width: 300 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" color="action" />
                            </InputAdornment>
                        ),
                    }}
                />

                <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleAddDealerClick} sx={{ whiteSpace: 'nowrap' }}>
                    Add Dealer
                </Button>
                <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleAddUserClick} sx={{ whiteSpace: 'nowrap' }}>
                    Add User
                </Button>
            </Box>
        </Toolbar>
    );
}

const UserRegistrationForm = ({ userData, handleChange, handleCreateRolesChange, availableRoles, handleUserCreateClick, handleClose }) => (
    <Box component="form" noValidate sx={{ mt: 1 }}>
        <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
                <TextField name="first_name" required fullWidth label="First Name" autoFocus value={userData.first_name} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField name="last_name" fullWidth label="Last Name" value={userData.last_name} onChange={handleChange} />
            </Grid>
            <Grid item xs={12}>
                <TextField name="username" required fullWidth label="Username" value={userData.username} onChange={handleChange} />
            </Grid>
            <Grid item xs={12}>
                <TextField name="email" required fullWidth label="Email Address" type="email" value={userData.email} onChange={handleChange} />
            </Grid>
            <Grid item xs={12}>
                <TextField name="password" required fullWidth label="Password" type="password" value={userData.password} onChange={handleChange} />
            </Grid>
            <Grid item xs={12}>
                <FormControl fullWidth sx={{ mt: 2 }}>
                    <InputLabel id="roles-label">Roles</InputLabel>
                    <Select
                        labelId="roles-label"
                        multiple
                        value={userData.roles}
                        onChange={handleCreateRolesChange}
                        input={<OutlinedInput label="Roles" />}
                        renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((value) => <Chip key={value} label={value} />)}
                            </Box>
                        )}
                    >
                        {availableRoles.map((role) => (
                            <MenuItem key={role} value={role}>
                                <Checkbox checked={userData.roles.indexOf(role) > -1} />
                                <ListItemText primary={role} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
            <Grid item xs={12}>
                <TextField name="dealer_code" required fullWidth label="Dealer Code" value={userData.dealer_code} onChange={handleChange} />
            </Grid>
        </Grid>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3, mb: 1 }}>
            <Button onClick={handleClose} color="inherit">Cancel</Button>
            <Button onClick={handleUserCreateClick} variant="contained" color="primary">Register</Button>
        </Box>
    </Box>
);

const DealerRegistrationForm = ({ dealerData, handleChange, handleDealerCreateClick, handleClose }) => (
    <Box component="form" noValidate sx={{ mt: 1 }}>
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <TextField name="dealer_name" required fullWidth label="Dealer Name" autoFocus value={dealerData.dealer_name} onChange={handleChange} />
            </Grid>
            <Grid item xs={12}>
                <TextField name="dealer_code" required fullWidth label="Dealer Code" value={dealerData.dealer_code} onChange={handleChange} />
            </Grid>
            <Grid item xs={12}>
                <TextField name="dealer_pincode" required fullWidth label="Dealer Pincode" value={dealerData.dealer_pincode} onChange={handleChange} />
            </Grid>
        </Grid>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3, mb: 1 }}>
            <Button onClick={handleClose} color="inherit">Cancel</Button>
            <Button onClick={handleDealerCreateClick} variant="contained" color="primary">Register</Button>
        </Box>
    </Box>
);

const UserEditForm = ({ userData, handleChange, handleEditRolesChange, availableRoles, handleUserUpdateClick, handleClose }) => (
    <Box component="form" noValidate sx={{ mt: 1 }}>
        <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
                <TextField name="first_name" required fullWidth label="First Name" autoFocus value={userData.first_name} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField name="last_name" fullWidth label="Last Name" value={userData.last_name} onChange={handleChange} />
            </Grid>
            <Grid item xs={12}>
                <TextField name="username" required fullWidth label="Username" value={userData.username} onChange={handleChange} />
            </Grid>
            <Grid item xs={12}>
                <TextField name="email" required fullWidth label="Email Address" type="email" value={userData.email} onChange={handleChange} />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    name="password"
                    fullWidth
                    label="Password"
                    type="password"
                    value={userData.password}
                    onChange={handleChange}
                    helperText="Leave blank to keep the current password"
                />
            </Grid>
            <Grid item xs={12}>
                <FormControl fullWidth sx={{ mt: 2 }}>
                    <InputLabel id="edit-roles-label">Roles</InputLabel>
                    <Select
                        labelId="edit-roles-label"
                        multiple
                        value={userData.roles}
                        onChange={handleEditRolesChange}
                        input={<OutlinedInput label="Roles" />}
                        renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((value) => <Chip key={value} label={value} />)}
                            </Box>
                        )}
                    >
                        {availableRoles.map((role) => (
                            <MenuItem key={role} value={role}>
                                <Checkbox checked={userData.roles.indexOf(role) > -1} />
                                <ListItemText primary={role} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
            <Grid item xs={12}>
                <TextField name="dealer_code" required fullWidth label="Dealer Code" value={userData.dealer_code} onChange={handleChange} />
            </Grid>
        </Grid>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3, mb: 1 }}>
            <Button onClick={handleClose} color="inherit">Cancel</Button>
            <Button onClick={handleUserUpdateClick} variant="contained" color="primary">Save Changes</Button>
        </Box>
    </Box>
);

export default function FlsUsers() {
    const [rows, setRows] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearchQuery] = useDebounce(searchQuery, 300);
    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState("username");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [errorMessage, setErrorMessage] = useState(null);

    const [availableRoles, setAvailableRoles] = useState([]);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

    // Dialog states
    const [openAddUser, setOpenAddUser] = useState(false);
    const [openAddDealer, setOpenAddDealer] = useState(false);
    const [openEditUser, setOpenEditUser] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [deleteEmail, setDeleteEmail] = useState("");
    const [editEmail, setEditEmail] = useState("");

    const [userData, setUserData] = useState({
        first_name: '', last_name: '', username: '', email: '', password: '', roles: [], dealer_code: '',
    });
    const [editUserData, setEditUserData] = useState({
        first_name: '', last_name: '', username: '', email: '', password: '', roles: [], dealer_code: '',
    });
    const [dealerData, setDealerData] = useState({
        dealer_name: '', dealer_code: '', dealer_pincode: '',
    });

    const fetchUsers = async () => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem("accessToken");
            const response = await api.get("/flightscan/v1/users", {
                headers: { Authorization: `Bearer ${token}` }
            });
            let usersList = response.data;
            setRows(usersList);
            setIsLoading(false);
        } catch (error) {
            console.error(error);
            setErrorMessage(error.response?.data?.message || "Error fetching data");
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const fetchRoles = async () => {
            try {
                const token = localStorage.getItem("accessToken");
                const response = await api.get("/flightscan/v1/users/roles", {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const rolesList = response.data.map(r => r.name || r.role_name);
                setAvailableRoles(rolesList);
            } catch (error) {
                console.error("Error fetching roles:", error);
            }
        };

        fetchUsers();
        fetchRoles();
    }, []);

    // Filter Logic
    const filteredRows = useMemo(() => {
        if (!debouncedSearchQuery) return rows;
        const lowerQuery = debouncedSearchQuery.toLowerCase();

        return rows.filter((row) => {
            const usernameMatch = (row.username?.toLowerCase() || "").includes(lowerQuery);
            const emailMatch = (row.email?.toLowerCase() || "").includes(lowerQuery);
            const firstNameMatch = (row.firstName?.toLowerCase() || "").includes(lowerQuery);
            const lastNameMatch = (row.lastName?.toLowerCase() || "").includes(lowerQuery);
            const dealerMatch = (row.Dealership.dealer_name?.toLowerCase() || "").includes(lowerQuery);

            let roleString = "";
            let rolesList = row.roles || row.Roles || [];
            if (rolesList.length > 0) {
                roleString = rolesList.map(r => r.name || r).join(" ");
            }
            const roleMatch = roleString.toLowerCase().includes(lowerQuery);

            return usernameMatch || emailMatch || firstNameMatch || lastNameMatch || roleMatch || dealerMatch;
        });
    }, [rows, debouncedSearchQuery]);

    useEffect(() => { setPage(0); }, [debouncedSearchQuery]);

    // Table Handlers
    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };
    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); };

    const handleFormChange = (e, formType) => {
        const { name, value } = e.target;
        if (formType === 'user') setUserData((prev) => ({ ...prev, [name]: value }));
        else if (formType === 'editUser') setEditUserData((prev) => ({ ...prev, [name]: value }));
        else setDealerData((prev) => ({ ...prev, [name]: value }));
    };

    const handleCreateRolesChange = (event) => {
        const { target: { value } } = event;
        setUserData((prev) => ({
            ...prev,
            roles: typeof value === 'string' ? value.split(',') : value,
        }));
    };

    const handleEditRolesChange = (event) => {
        const { target: { value } } = event;
        setEditUserData((prev) => ({
            ...prev,
            roles: typeof value === 'string' ? value.split(',') : value,
        }));
    };

    const handleEditUserClick = (row) => {
        setEditEmail(row.email);
        setEditUserData({
            first_name: row.firstName || '',
            last_name: row.lastName || '',
            username: row.username || '',
            email: row.email || '',
            password: '',
            roles: (row.Roles || []).map((r) => r.role_name || r.name || r),
            dealer_code: row.Dealership?.dealer_code || '',
        });
        setOpenEditUser(true);
    };

    const handleUserUpdateClick = async () => {
        if (!editUserData.first_name || !editUserData.username || !editUserData.email || editUserData.roles.length === 0 || !editUserData.dealer_code) {
            setSnackbar({ open: true, message: "Please fill all required fields.", severity: "warning" });
            return;
        }
        const token = localStorage.getItem("accessToken");
        const payload = { ...editUserData };
        if (!payload.password) delete payload.password;
        try {
            await api.put(`/flightscan/v1/users/${editEmail}`, payload, { headers: { Authorization: `Bearer ${token}` } });
            setSnackbar({ open: true, message: "User updated successfully!", severity: "success" });
            setOpenEditUser(false);
            fetchUsers();
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.errors || err.response?.data?.message || "Failed to update user.", severity: "error" });
        }
    };

    const handleUserCreateClick = async () => {
        if (!userData.first_name || !userData.password || !userData.username || !userData.email || userData.roles.length === 0 || !userData.dealer_code) {
            setSnackbar({ open: true, message: "Please fill all required fields.", severity: "warning" });
            return;
        }
        const token = localStorage.getItem("accessToken");
        try {
            await api.post("/flightscan/v1/users/register", userData, { headers: { Authorization: `Bearer ${token}` } });
            setSnackbar({ open: true, message: "User created successfully!", severity: "success" });
            setUserData({ first_name: '', last_name: '', username: '', email: '', password: '', roles: [], dealer_code: '' });
            setOpenAddUser(false);
            fetchUsers();
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.errors || err.response?.data?.message || "Failed to create user.", severity: "error" });
        }
    };

    const handleDealerCreateClick = async () => {
        if (!dealerData.dealer_name || !dealerData.dealer_code || !dealerData.dealer_pincode) {
            setSnackbar({ open: true, message: "Please fill all required fields.", severity: "warning" });
            return;
        }
        const token = localStorage.getItem("accessToken");
        try {
            await api.post("/flightscan/v1/dealers", dealerData, { headers: { Authorization: `Bearer ${token}` } });
            setSnackbar({ open: true, message: "Dealer created successfully!", severity: "success" });
            setDealerData({ dealer_name: '', dealer_code: '', dealer_pincode: '' });
            setOpenAddDealer(false);
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.errors || err.response?.data?.message || "Failed to create dealer.", severity: "error" });
        }
    };

    const handleUserDeleteClick = async () => {
        const token = localStorage.getItem("accessToken");
        try {
            await api.delete(`/flightscan/v1/users/${deleteEmail}`, { headers: { Authorization: `Bearer ${token}` } });
            setSnackbar({ open: true, message: "User deleted successfully!", severity: "success" });
            setOpenDeleteDialog(false);
            fetchUsers();
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.errors || err.response?.data?.message || "Failed to delete user.", severity: "error" });
            setOpenDeleteDialog(false);
        }
    };

    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredRows.length) : 0;
    const visibleRows = useMemo(() => [...filteredRows].sort(getComparator(order, orderBy)).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage), [order, orderBy, page, rowsPerPage, filteredRows]);

    return (
        <Box sx={{ width: "100%" }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Paper sx={{ width: "100%", mb: 2, mt: 2 }}>

                    <EnhancedTableToolbar
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                        handleAddUserClick={() => setOpenAddUser(true)}
                        handleAddDealerClick={() => setOpenAddDealer(true)}
                    />

                    <TableContainer>
                        <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size="medium">
                            <EnhancedTableHead order={order} orderBy={orderBy} onRequestSort={handleRequestSort} />
                            <TableBody>
                                {isLoading ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                                            <CircularProgress />
                                        </TableCell>
                                    </TableRow>
                                ) : visibleRows.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center">
                                            <Alert severity="info">{errorMessage || "No data available."}</Alert>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    visibleRows.map((row, index) => {
                                        return (
                                            <TableRow hover tabIndex={-1} key={row.email || index}>
                                                <TableCell sx={{ pl: 3 }}>{row.username || "N/A"}</TableCell>
                                                <TableCell>{row.email}</TableCell>
                                                <TableCell>{row.firstName} {row.lastName || ""}</TableCell>
                                                <TableCell>{row.Dealership.dealer_name}</TableCell>
                                                <TableCell>
                                                    {(() => {
                                                        const rolesList = row.Roles || [];
                                                        if (rolesList.length === 0) return <Typography variant="body2" color="textSecondary">No roles</Typography>;
                                                        return rolesList.map((r, idx) => (
                                                            <Chip key={idx} label={r.role_name || r} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                                                        ));
                                                    })()}
                                                </TableCell>
                                                <TableCell>
                                                    <Tooltip title="Edit User">
                                                        <IconButton
                                                            onClick={(e) => { e.stopPropagation(); handleEditUserClick(row); }}
                                                            size="small" color="primary"
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete User">
                                                        <IconButton
                                                            onClick={(e) => { e.stopPropagation(); setDeleteEmail(row.email); setOpenDeleteDialog(true); }}
                                                            size="small" color="error"
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                                {emptyRows > 0 && (
                                    <TableRow style={{ height: 53 * emptyRows }}>
                                        <TableCell colSpan={5} />
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={filteredRows.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Paper>
            </Box>

            {/* Add User Dialog */}
            <Dialog open={openAddUser} onClose={() => setOpenAddUser(false)} fullWidth maxWidth="sm">
                <DialogTitle>Register User</DialogTitle>
                <DialogContent dividers>
                    <UserRegistrationForm
                        userData={userData}
                        handleChange={(e) => handleFormChange(e, 'user')}
                        handleCreateRolesChange={handleCreateRolesChange}
                        availableRoles={availableRoles}
                        handleUserCreateClick={handleUserCreateClick}
                        handleClose={() => setOpenAddUser(false)}
                    />
                </DialogContent>
            </Dialog>

            {/* Edit User Dialog */}
            <Dialog open={openEditUser} onClose={() => setOpenEditUser(false)} fullWidth maxWidth="sm">
                <DialogTitle>Edit User</DialogTitle>
                <DialogContent dividers>
                    <UserEditForm
                        userData={editUserData}
                        handleChange={(e) => handleFormChange(e, 'editUser')}
                        handleEditRolesChange={handleEditRolesChange}
                        availableRoles={availableRoles}
                        handleUserUpdateClick={handleUserUpdateClick}
                        handleClose={() => setOpenEditUser(false)}
                    />
                </DialogContent>
            </Dialog>

            {/* Add Dealer Dialog */}
            <Dialog open={openAddDealer} onClose={() => setOpenAddDealer(false)} fullWidth maxWidth="sm">
                <DialogTitle>Register Dealer</DialogTitle>
                <DialogContent dividers>
                    <DealerRegistrationForm
                        dealerData={dealerData}
                        handleChange={(e) => handleFormChange(e, 'dealer')}
                        handleDealerCreateClick={handleDealerCreateClick}
                        handleClose={() => setOpenAddDealer(false)}
                    />
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete user with email "{deleteEmail}"?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDeleteDialog(false)} color="inherit">Cancel</Button>
                    <Button onClick={handleUserDeleteClick} variant="contained" color="error">Delete</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}